import { apiFetch } from './api'
import { toast } from 'sonner'

export interface CancellationResult {
  success: boolean
  data?: {
    orderId: string
    status: string
    results?: Array<{
      itemId: string
      type: string
      status: string
      error?: string
    }>
  }
  error?: string
  details?: string
}

/**
 * Cancel a hotel booking
 * @param orderId - RateHawk order ID or our order item ID
 * @returns Cancellation result
 */
export async function cancelHotelBooking(orderId: string): Promise<CancellationResult> {
  try {
    const response = await apiFetch(`/api/hotels/cancel/${orderId}`, {
      method: 'POST',
      abortKey: `cancel-booking-${orderId}`
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || error.details || 'Failed to cancel booking')
    }

    const result = await response.json()
    return result as CancellationResult
  } catch (error) {
    console.error('[cancelHotelBooking] Error:', error)
    const errorMessage = error instanceof Error ? error.message : 'Failed to cancel booking'
    toast.error('Cancellation Failed', {
      description: errorMessage
    })
    return {
      success: false,
      error: errorMessage
    }
  }
}

/**
 * Cancel an entire order (all items)
 * @param orderId - Our order ID
 * @returns Cancellation result
 */
export async function cancelOrder(orderId: string): Promise<CancellationResult> {
  try {
    const response = await apiFetch('/api/orchestration/cancel', {
      method: 'POST',
      body: JSON.stringify({ orderId }),
      abortKey: `cancel-order-${orderId}`
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to cancel order')
    }

    const result = await response.json()
    return result as CancellationResult
  } catch (error) {
    console.error('[cancelOrder] Error:', error)
    const errorMessage = error instanceof Error ? error.message : 'Failed to cancel order'
    toast.error('Cancellation Failed', {
      description: errorMessage
    })
    return {
      success: false,
      error: errorMessage
    }
  }
}

// ─── VOY-397: Duffel 2-step flight cancellation ────────────────────

// VOY-616: refundAmount is what goes back to the customer's card through Stripe, not what the
// airline returns to Voy. chargedAmount is what the customer paid for the flight and
// nonRefundableAmount the part that stays (airline penalty and Voy's service fee).
export interface FlightCancellationQuote {
  id: string
  orderId: string
  refundAmount: string
  refundCurrency: string
  refundTo: string
  chargedAmount: string | null
  nonRefundableAmount: string | null
  expiresAt: string | null
  confirmedAt: string | null
  createdAt: string
}

export interface FlightCancellationConfirmResult {
  id: string
  orderId: string
  /** Null while the refund is pending. */
  refundAmount: string | null
  refundCurrency: string | null
  refundTo: string
  /** 'processing': Stripe has the refund and is settling it. 'pending': it could not be created yet. */
  refundStatus: 'refunded' | 'processing' | 'not_refundable' | 'pending'
  confirmedAt: string
  warning?: string
}

// VOY-616: off unless VITE_ENABLE_FLIGHT_CANCELLATION=true. Online cancellation needs the backend
// that refunds the card; an environment without it must not offer the button.
export const FLIGHT_CANCELLATION_ENABLED = import.meta.env.VITE_ENABLE_FLIGHT_CANCELLATION === 'true'

// VOY-567: these calls name OUR order_items row. The backend checks that it belongs to the
// signed-in user and reads every Duffel id (order, cancellation, change) from the database.

/**
 * Step 1: Request a cancellation quote for a booked flight.
 * Returns the refund amount (net of airline penalties) and expiry.
 */
export async function requestFlightCancellationQuote(
  orderItemId: string,
): Promise<FlightCancellationQuote> {
  const response = await apiFetch('/api/flights/cancel/quote', {
    method: 'POST',
    body: JSON.stringify({ orderItemId }),
    abortKey: `cancel-quote-${orderItemId}`,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => null)
    throw new Error(err?.error || `Cancellation quote failed (${response.status})`)
  }
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Cancellation quote failed')
  return result.data as FlightCancellationQuote
}

/**
 * Step 2: Confirm the cancellation after the user reviews the quote.
 * The backend confirms the quote it issued for this item in step 1.
 */
export async function confirmFlightCancellation(
  orderItemId: string,
): Promise<FlightCancellationConfirmResult> {
  const response = await apiFetch('/api/flights/cancel/confirm', {
    method: 'POST',
    body: JSON.stringify({ orderItemId }),
    abortKey: `cancel-confirm-${orderItemId}`,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => null)
    throw new Error(err?.error || `Cancellation confirmation failed (${response.status})`)
  }
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Cancellation confirmation failed')
  return {
    ...result.data,
    warning: result.warning,
  } as FlightCancellationConfirmResult
}

// ─── VOY-398: Duffel 3-step flight order changes ───────────────────

// VOY-567: off for launch. The backend answers 403 FLIGHT_CHANGES_DISABLED unless its own
// FLIGHT_CHANGES_ENABLED flag is on, so this only decides whether the button is shown.
export const FLIGHT_CHANGES_ENABLED = import.meta.env.VITE_ENABLE_FLIGHT_CHANGES === 'true'

export interface OrderChangeOffer {
  id: string
  changeTotalAmount: string
  changeTotalCurrency: string
  newTotalAmount: string
  newTotalCurrency: string
  penaltyTotalAmount: string
  penaltyTotalCurrency: string
  refundTo: string | null
  expiresAt: string | null
  slices: unknown[]
}

export interface OrderChangeRequestResult {
  id: string
  orderId: string
  orderChangeOffers: OrderChangeOffer[]
}

export interface OrderChangeConfirmResult {
  id: string
  orderId: string
  changeTotalAmount: string
  newTotalAmount: string
  confirmedAt: string
  warning?: string
}

/**
 * Step 1: Request change options for a booked flight.
 * Returns available change offers with pricing.
 */
export async function requestFlightChangeOptions(
  orderItemId: string,
  slices: { remove: Array<{ slice_id: string }>; add: Array<{ origin: string; destination: string; departure_date: string }> },
): Promise<OrderChangeRequestResult> {
  const response = await apiFetch('/api/flights/change/request', {
    method: 'POST',
    body: JSON.stringify({ orderItemId, slices }),
    abortKey: `change-request-${orderItemId}`,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => null)
    throw new Error(err?.error || `Change request failed (${response.status})`)
  }
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Change request failed')
  return result.data as OrderChangeRequestResult
}

/**
 * Step 2: Select a change offer (creates a pending order change).
 */
export async function selectFlightChangeOffer(
  orderItemId: string,
  selectedOrderChangeOfferId: string,
): Promise<void> {
  const response = await apiFetch('/api/flights/change/select', {
    method: 'POST',
    body: JSON.stringify({ orderItemId, selectedOrderChangeOfferId }),
    abortKey: `change-select-${selectedOrderChangeOfferId}`,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => null)
    throw new Error(err?.error || `Change selection failed (${response.status})`)
  }
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Change selection failed')
}

/**
 * Step 3: Confirm the change selected in step 2. No payment is sent: the backend only confirms
 * a change with no price difference and answers 409 CHANGE_PAYMENT_REQUIRED otherwise.
 */
export async function confirmFlightChange(
  orderItemId: string,
): Promise<OrderChangeConfirmResult> {
  const response = await apiFetch('/api/flights/change/confirm', {
    method: 'POST',
    body: JSON.stringify({ orderItemId }),
    abortKey: `change-confirm-${orderItemId}`,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => null)
    throw new Error(err?.error || `Change confirmation failed (${response.status})`)
  }
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Change confirmation failed')
  return {
    ...result.data,
    warning: result.warning,
  } as OrderChangeConfirmResult
}

// ─── VOY-347: Ancillaries ───────────────────────────────────────────

/**
 * Fetch a Duffel offer with available_services + seat maps.
 * Returns the raw shapes the DuffelAncillaries component needs.
 */
export async function fetchOfferAncillaries(
  offerId: string,
): Promise<{ offer: unknown; seat_maps: unknown[] }> {
  const response = await apiFetch(`/api/flights/offer/${offerId}/ancillaries`, {
    method: 'GET',
    abortKey: `ancillaries-${offerId}`,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => null)
    throw new Error(err?.error || `Failed to fetch ancillaries (${response.status})`)
  }
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Failed to fetch ancillaries')
  return result.data as { offer: unknown; seat_maps: unknown[] }
}

/**
 * Get cancellation rules for a prebook
 * @param prebookId - Prebook ID (optional, can be in session)
 * @returns Cancellation rules
 */
export async function getCancellationRules(prebookId?: string): Promise<Record<string, unknown> | null> {
  try {
    const url = prebookId 
      ? `/api/hotels/cancellation-rules?prebook_id=${prebookId}`
      : '/api/hotels/cancellation-rules'
    
    const response = await apiFetch(url, {
      method: 'GET',
      abortKey: `cancellation-rules-${prebookId || 'session'}`
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to get cancellation rules')
    }

    const result = await response.json()
    return result.data
  } catch (error) {
    console.error('[getCancellationRules] Error:', error)
    return null
  }
}

